import { useEffect, useState } from 'react';
import './App.css';
import CheckoutPage from './components/CheckoutPage';
import ConfirmPage from './components/ConfirmPage';
import Footer from './components/Footer';
import Header from './components/Header';
import HomePage from './components/HomePage';
import { AuthModal } from './components/Modals';
import PaymentPage from './components/PaymentPage';
import PropertyPage from './components/PropertyPage';
import RateListPage from './components/RateListPage';

const initialSearch = {
  destination: 'Dar es Salaam',
  checkIn: null,
  checkOut: null,
  occupancy: { adults: 2 },
};

function App() {
  const [view, setView] = useState('home');
  const [search, setSearch] = useState(initialSearch);
  const [property, setProperty] = useState(null);
  const [guest, setGuest] = useState(null);
  const [booking, setBooking] = useState(null);
  const [authMode, setAuthMode] = useState(null);
  const [toast, setToast] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const loader = document.getElementById('boot-loader');
    if (!loader) return undefined;

    let hideTimer;
    let waitTimer;
    const hide = () => {
      loader.classList.add('is-done');
      document.documentElement.classList.remove('is-loading');
      hideTimer = window.setTimeout(() => loader.remove(), 480);
    };

    const started = Date.now();
    const finish = () => {
      waitTimer = window.setTimeout(hide, Math.max(0, 900 - (Date.now() - started)));
    };

    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', finish, { once: true });

    return () => {
      window.removeEventListener('load', finish);
      window.clearTimeout(waitTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const goHome = () => {
    setView('home');
    setProperty(null);
    setGuest(null);
    scrollTop();
  };

  const runSearch = (place) => {
    if (typeof place === 'string') setSearch((s) => ({ ...s, destination: place }));
    setView('rates');
    scrollTop();
  };

  const openProperty = (item) => {
    setProperty(item);
    if (!search.destination) setSearch((s) => ({ ...s, destination: item.city }));
    setView('property');
    scrollTop();
  };

  const openCheckout = (room) => {
    setProperty(room);
    setView('checkout');
    scrollTop();
  };

  const saveGuest = (nextGuest) => {
    setGuest(nextGuest);
    setView('payment');
    scrollTop();
  };

  const confirmBooking = (payment) => {
    setBooking({
      guest,
      room: property,
      search,
      payment,
      code: `KSV-${property.id.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`,
    });
    setView('confirm');
    scrollTop();
  };

  return (
    <div className="site">
      <Header
        onHome={goHome}
        onOpenAuth={setAuthMode}
        onToast={setToast}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        user={user}
      />

      {view === 'home' && (
        <HomePage
          onOpenProperty={openProperty}
          search={search}
          setSearch={setSearch}
          onSearch={runSearch}
        />
      )}
      {view === 'rates' && (
        <RateListPage
          search={search}
          setSearch={setSearch}
          onSearch={runSearch}
          onHome={goHome}
          onBook={openCheckout}
        />
      )}
      {view === 'property' && property && (
        <PropertyPage
          property={property}
          search={search}
          onSearch={runSearch}
          onHome={goHome}
          onBook={() => openCheckout(property)}
        />
      )}
      {view === 'checkout' && property && (
        <CheckoutPage
          search={search}
          room={property}
          user={user}
          guest={guest}
          onBack={() => {
            setView('property');
            scrollTop();
          }}
          onContinue={saveGuest}
        />
      )}
      {view === 'payment' && property && guest && (
        <PaymentPage
          search={search}
          room={property}
          guest={guest}
          onBack={() => {
            setView('checkout');
            scrollTop();
          }}
          onConfirm={confirmBooking}
        />
      )}
      {view === 'confirm' && booking && (
        <ConfirmPage booking={booking} onHome={goHome} />
      )}

      <Footer onToast={setToast} />

      {authMode && (
        <AuthModal
          mode={authMode}
          setMode={setAuthMode}
          onClose={() => setAuthMode(null)}
          onSuccess={(email) => {
            setUser(email);
            setAuthMode(null);
            setToast(`Signed in as ${email}`);
          }}
        />
      )}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

export default App;
